import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyledBackButton, StyledBreadcrumb, StyledBreadcrumbContainer, StyledMainContainer, StyledSlash } from './Breadcrumbs.styles';
import type { MinimalCatalogCategory } from '../../state/Catalog';
import { useCatalogContext } from '../../state/Catalog';
import type { CatalogDepartmentDictionaryEntry } from '../../state/Catalog/Catalog.types';
import type { FlexProps } from '@withjoy/joykit';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { SkeletonText } from '@shared/components/Skeleton';
import { withWindow } from '@shared/utils/withWindow';
import type { CatalogCategoryType, PagesForTelemetry } from '../..';
import { useCatalogDepartments } from '../../hooks/useCatalogDepartments/useCatalogDepartments';
import { ReactComponent as ChevronBackIcon } from '@shared/joykit/packages/core/icons/svgs/16/chevron-back.svg';
import { pxToRem } from '@withjoy/joykit/theme';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useTranslation } from '@shared/core';
import { useCatalogEmbed } from '../../state/CatalogEmbed/useCatalogEmbed';

export interface BreadcrumbsProps
  extends Readonly<{
    categoryId: Maybe<string>;
    color?: string | null;
    isMobile?: boolean;
    hideLastBreadcrumb: boolean;
    defaultAlign: string;
    telemetryPageValue: PagesForTelemetry;
    endElement?: React.ReactNode;
    showEndElement?: boolean;
    allowNavigationOnLastBreadcrumb?: boolean;
  }> {}

type BreadcrumbEntry = {
  slash: boolean;
  category: MinimalCatalogCategory;
  categoryType: CatalogCategoryType;
};

const Breadcrumbs = ({
  categoryId,
  isMobile,
  hideLastBreadcrumb,
  defaultAlign,
  telemetryPageValue,
  endElement,
  showEndElement = true,
  allowNavigationOnLastBreadcrumb,
}: BreadcrumbsProps) => {
  const { getDepartmentByIdOrPath } = useCatalogDepartments();
  const { tq } = useTranslation('catalogRegistry');
  const embed = useCatalogEmbed();

  const category = getDepartmentByIdOrPath(categoryId);
  const { goToCategory } = useCatalogContext();
  const breadCrumbContainerRef = useRef<HTMLDivElement>(null);
  const [isFixedTop, setIsFixedTop] = useState(false);
  const { trackCategoryNavigateAction } = useCatalogLoggingContext();

  const handleOnScroll = useCallback(() => {
    if (breadCrumbContainerRef.current !== null && !isMobile) {
      const { top: breadCrumbContainerRefTop } = breadCrumbContainerRef.current.getBoundingClientRect();
      setIsFixedTop(breadCrumbContainerRefTop <= 0);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setIsFixedTop(false);
    }
  }, [isMobile]);

  useEffect(() => {
    withWindow(window => {
      window.addEventListener('scroll', handleOnScroll);
    });
    return () => {
      withWindow(window => {
        window.removeEventListener('scroll', handleOnScroll);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breadcrumbs = useMemo(() => {
    const breadcrumbs: Array<BreadcrumbEntry> = [];
    if (category) {
      const lineage = (category as CatalogDepartmentDictionaryEntry).lineageCsp;
      if (lineage) {
        const rootDepartment = lineage[0];
        if (rootDepartment) {
          let shopDepartment = rootDepartment;
          if (rootDepartment.title.toLowerCase() === 'root' || rootDepartment.title.toLowerCase() === 'baby') {
            shopDepartment = {
              ...rootDepartment,
              title: tq`Shop`,
            };
          }
          breadcrumbs.push({ slash: false, category: shopDepartment, categoryType: 'root' });
        }
        const lineageDepartment = lineage[1];
        if (lineageDepartment) {
          breadcrumbs.push({ slash: true, category: lineageDepartment, categoryType: 'department' });
        }
        const lineageCategory = lineage[2];
        if (lineageCategory) {
          breadcrumbs.push({ slash: true, category: lineageCategory, categoryType: 'category' });
        }
        if (!hideLastBreadcrumb) {
          if (category.id !== lineage[lineage.length - 1].id) {
            breadcrumbs.push({ slash: true, category, categoryType: 'subcategory' });
          }
        }
      }
    }
    if (embed) {
      // In an embedded catalog (e.g. lean international Add Gifts) the trail must not expose ancestors
      // above the embed's allowed roots — no "Shop" crumb, and no broader parent department the user
      // is not allowed to browse. Start the trail at the allowed root; fall back to dropping the root.
      const allowedRootPaths = new Set(embed.allowedRootPublicPaths);
      const rootIndex = breadcrumbs.findIndex(bc => allowedRootPaths.has(bc.category.publicUrlPath));
      const trimmed = rootIndex >= 0 ? breadcrumbs.slice(rootIndex) : breadcrumbs.slice(1);
      if (trimmed.length > 0) {
        trimmed[0] = { ...trimmed[0], slash: false };
      }
      return trimmed;
    }
    return breadcrumbs;
  }, [category, hideLastBreadcrumb, tq, embed]);

  if (!category) {
    return null;
  }

  const previousLocationBreadcrumb = hideLastBreadcrumb ? breadcrumbs[breadcrumbs.length - 1] : breadcrumbs[breadcrumbs.length - 2];

  const trackAndNavigate = (breadcrumb: BreadcrumbEntry) => {
    window.scrollTo({ top: 0 });
    trackCategoryNavigateAction({
      category: breadcrumb.category,
      navigationSource: 'breadcrumbs',
      overrides: {
        page: telemetryPageValue,
      },
    });
    goToCategory(breadcrumb.category.publicUrlPath);
  };

  return (
    <StyledMainContainer
      ref={breadCrumbContainerRef}
      width={['100%', null, 'unset']}
      justifyContent="space-between"
      height={[null, null, pxToRem(56)]}
    >
      <StyledBreadcrumbContainer
        __css={{
          '&.fixed-top': {
            paddingX: [6, null, 7],
          },
        }}
        className={isFixedTop ? 'fixed-top' : ''}
        defaultAlign={defaultAlign}
        zIndex={1}
        isMobile={isMobile}
      >
        {isMobile ? (
          previousLocationBreadcrumb ? (
            <StyledBackButton
              color="mono14"
              startIcon={ChevronBackIcon}
              variant="link"
              onClick={() => trackAndNavigate(previousLocationBreadcrumb)}
            >
              <TextV2 typographyVariant="hed2">{previousLocationBreadcrumb.category.title}</TextV2>
            </StyledBackButton>
          ) : null
        ) : (
          breadcrumbs.map((bc, index) => {
            const isCurrentPageBreadcrumb = !hideLastBreadcrumb && index === breadcrumbs.length - 1;
            return (
              <Box key={bc.category.id}>
                {bc.slash && (
                  <StyledSlash
                    isMobile={isMobile}
                    color={'typeTertiaryDark'}
                  >
                    /
                  </StyledSlash>
                )}
                <StyledBreadcrumb
                  className={isCurrentPageBreadcrumb ? 'current-page' : ''}
                  onClick={isCurrentPageBreadcrumb && !allowNavigationOnLastBreadcrumb ? undefined : () => trackAndNavigate(bc)}
                  allowNavigationOnCurrentPage={allowNavigationOnLastBreadcrumb}
                >
                  {bc.category.title}
                </StyledBreadcrumb>
              </Box>
            );
          })
        )}
        {endElement && isFixedTop && showEndElement ? <Box marginLeft="auto">{endElement}</Box> : null}
      </StyledBreadcrumbContainer>
    </StyledMainContainer>
  );
};
Breadcrumbs.Skeleton = (props: FlexProps) => {
  return (
    <Flex
      alignItems="center"
      color={'mono5'}
      {...props}
    >
      <SkeletonText
        width="48px"
        skeletonHeight="14px"
        rows={1}
      />
      &nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;
      <SkeletonText
        width="64px"
        skeletonHeight="14px"
        rows={1}
      />
      &nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;
      <SkeletonText
        width="100px"
        skeletonHeight="14px"
        rows={1}
      />
      &nbsp;&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;&nbsp;
      <SkeletonText
        width="140px"
        skeletonHeight="14px"
        rows={1}
      />
    </Flex>
  );
};
Breadcrumbs.displayName = 'CatalogBreadcrumbs';
export { Breadcrumbs };
